import { ReactElement, useEffect } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { LatLngBounds, Map as LeafletMap } from 'leaflet';

// Leaflet's getBoundsZoom returns Infinity for zero-extent bounds (a single marker, or
// coincident markers) and for a container not yet laid out. fitBounds then calls
// setView(center, Infinity), which is only clamped by the map's maxZoom -- and that is
// still Infinity here, because FitBounds' effect runs before the sibling TileLayer has
// been added. A numeric maxZoom on the call itself is what keeps the map's zoom finite.
const FIT_BOUNDS_MAX_ZOOM = 17;

export function FitBounds({
  bounds,
  trigger,
}: {
  bounds: LatLngBounds | null;
  trigger: number;
}): ReactElement | null {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: FIT_BOUNDS_MAX_ZOOM });
    }
  }, [map, bounds, trigger]);

  return null;
}

export function BoundsListener({
  onBoundsChange,
}: {
  onBoundsChange: (shouldShow: boolean) => void;
}): ReactElement | null {
  const map = useMapEvents({
    zoomend: () => handleBoundsChange(map, onBoundsChange),
    moveend: () => handleBoundsChange(map, onBoundsChange),
  });

  useEffect(() => {
    handleBoundsChange(map, onBoundsChange);
  }, [map, onBoundsChange]);

  return null;
}

function handleBoundsChange(map: LeafletMap, onBoundsChange: (shouldShow: boolean) => void): void {
  const bounds = map.getBounds();
  if (!bounds.isValid()) return;
  const diagonalDistanceMeters = bounds.getNorthEast().distanceTo(bounds.getSouthWest());
  onBoundsChange(diagonalDistanceMeters <= 10_000);
}

/**
 * Keep Leaflet in step with its container's size.
 *
 * Leaflet measures the container once, when the map mounts. If the container then
 * changes size (a layout that settles after data loads, a sticky panel sized to the
 * viewport, a window resize), the tiles fill only the area it first measured, which
 * shows as a map rendered in its top half. The SVG overlay pane keeps the old
 * size too, so route lines and markers outside it are not redrawn and appear stuck
 * while panning. invalidateSize() re-measures and redraws both.
 */
export function MapAutoResize(): null {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => map.invalidateSize({ pan: false }));
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [map]);

  return null;
}
